import { Shield, AlertTriangle, CheckCircle, X } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { OFFICIAL_PHONE, OFFICIAL_DOMAIN, WHATSAPP_URL } from "@/data/constants";

const GOLD = "#B89047";
const CHARCOAL = "#1A1A1A";
const OFF_WHITE = "#FDFCF8";
const BORDER = "#E5E0D8";

export default function VerifyPage() {
  useSEO({
    title: "Verify Authenticity | Akkshay Narula — Official Domain & Contact",
    description: "Protect yourself from fraud. Verify that you are on the official Akkshay Narula website and contacting the genuine number before making any payment. Anti-fraud verification page.",
    keywords: "verify astrologer India, authentic astrologer Akkshay Narula, official website Akkshay Narula",
    canonical: "/verify",
  });
  return (
    <main data-testid="verify-page" style={{ backgroundColor: OFF_WHITE }}>
      {/* Header */}
      <section style={{ padding: "72px 0 56px", backgroundColor: CHARCOAL }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px" }}>
            <Shield size={22} style={{ color: GOLD }} />
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>
              Anti-Fraud Protection
            </p>
          </div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 5vw, 3.8rem)", fontWeight: 500, color: OFF_WHITE, lineHeight: 1.1, maxWidth: "680px" }}>
            Verify Authenticity Before You Pay
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#8A8A8A", marginTop: "16px", lineHeight: 1.7, maxWidth: "560px" }}>
            Fraudsters create fake profiles impersonating Akkshay Narula. Protect yourself by verifying only through official channels.
          </p>
        </div>
      </section>

      {/* Official Channels */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "40px" }}>
            Official Channels Only
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Official Website */}
            <div
              data-testid="official-website-card"
              style={{ border: `2px solid ${GOLD}`, padding: "40px", backgroundColor: "#FFFDF5" }}
            >
              <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "20px" }}>
                <CheckCircle size={20} style={{ color: GOLD }} />
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>
                  Verified Official
                </p>
              </div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#8A8A8A", marginBottom: "8px" }}>
                Official Website
              </p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", fontWeight: 500, color: CHARCOAL, marginBottom: "12px" }}>
                {OFFICIAL_DOMAIN}
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "#4A4A4A", lineHeight: 1.65 }}>
                The only official website. Check the URL bar of your browser to confirm you are on this exact domain before proceeding with any payment.
              </p>
            </div>

            {/* Official WhatsApp */}
            <div
              data-testid="official-whatsapp-card"
              style={{ border: `2px solid ${GOLD}`, padding: "40px", backgroundColor: "#FFFDF5" }}
            >
              <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "20px" }}>
                <CheckCircle size={20} style={{ color: GOLD }} />
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 600 }}>
                  Verified Official
                </p>
              </div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "#8A8A8A", marginBottom: "8px" }}>
                Official WhatsApp Number
              </p>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", fontWeight: 500, color: CHARCOAL, marginBottom: "12px" }}>
                +91 {OFFICIAL_PHONE}
              </p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.85rem", color: "#4A4A4A", lineHeight: 1.65 }}>
                The only official contact number. All bookings and consultations are conducted directly through this number only.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fraud Warning */}
      <section style={{ padding: "72px 0", backgroundColor: "#F5F3EB", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "40px" }}>
            <AlertTriangle size={20} style={{ color: "#c0392b" }} />
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "#c0392b", fontWeight: 600 }}>
              Warning — Fraud Awareness
            </p>
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "40px" }}>
            What Fraudsters Do
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {[
              "Create fake social media profiles using Akkshay Narula's name and photos",
              "Claim to be assistants, agents, or representatives of Akkshay Narula",
              "Offer 'discounts' or 'free consultations' to lure victims",
              "Ask for payment to personal UPI IDs not linked to official accounts",
              "Operate on unofficial domains that resemble the real website",
              "Use multiple phone numbers and Telegram accounts to deceive",
            ].map((warning, i) => (
              <div
                key={i}
                data-testid={`fraud-warning-${i + 1}`}
                style={{ display: "flex", gap: "14px", padding: "20px 0", borderBottom: `1px solid ${BORDER}`, alignItems: "flex-start", paddingRight: i % 2 === 0 ? "40px" : "0" }}
              >
                <X size={16} style={{ color: "#c0392b", flexShrink: 0, marginTop: "3px" }} />
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A", lineHeight: 1.7 }}>
                  {warning}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Verify */}
      <section style={{ padding: "72px 0", borderBottom: `1px solid ${BORDER}` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", color: GOLD, fontWeight: 600, marginBottom: "16px" }}>
            Stay Safe
          </p>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 500, color: CHARCOAL, marginBottom: "40px" }}>
            How to Verify Before Paying
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {[
              { num: "01", title: "Check the Domain", desc: `Ensure you are booking on ${OFFICIAL_DOMAIN} only. Do not trust any other website or redirect link.` },
              { num: "02", title: "Save the Number", desc: `Save +91 ${OFFICIAL_PHONE} as the only official contact. Do not pay to any other number.` },
              { num: "03", title: "No Agents Exist", desc: "Akkshay Narula does not work through agents or third parties. All consultations are booked and conducted directly." },
            ].map((step, i) => (
              <div
                key={step.num}
                data-testid={`verify-step-${i + 1}`}
                style={{ padding: "40px", borderRight: i < 2 ? `1px solid ${BORDER}` : "none", borderBottom: `1px solid ${BORDER}` }}
              >
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "3rem", fontWeight: 400, color: BORDER, lineHeight: 1, marginBottom: "16px" }}>
                  {step.num}
                </p>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", fontWeight: 500, color: CHARCOAL, marginBottom: "12px" }}>
                  {step.title}
                </h3>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.88rem", color: "#4A4A4A", lineHeight: 1.75 }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Notice */}
      <section style={{ backgroundColor: CHARCOAL, padding: "64px 0" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Shield size={32} style={{ color: GOLD, margin: "0 auto 20px" }} />
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 4vw, 2.4rem)", fontWeight: 500, color: OFF_WHITE, marginBottom: "16px" }}>
            When in Doubt, Don't Pay
          </h2>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: "0.9rem", color: "#8A8A8A", lineHeight: 1.75, marginBottom: "32px" }}>
            If you receive a message from someone claiming to be Akkshay Narula or his representative from an unverified number or platform, do not make any payment. Contact us directly to verify.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="verify-contact-whatsapp-btn"
            style={{ backgroundColor: GOLD, color: CHARCOAL, padding: "14px 32px", fontFamily: "'Outfit', sans-serif", fontSize: "0.75rem", letterSpacing: "0.12em", textTransform: "uppercase", textDecoration: "none", fontWeight: 600 }}
          >
            Verify via Official WhatsApp
          </a>
        </div>
      </section>
    </main>
  );
}
